import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Play, Pause, ArrowDown, ArrowRight } from "lucide-react";
import { api, resolveMedia } from "../lib/api";
import { usePlayer } from "../context/AudioContext";
import { useSite } from "../context/SiteContext";
import { useSEO } from "../lib/useSEO";
import { Reveal } from "../components/Reveal";
import { SectionLabel } from "../components/SectionLabel";
import { Marquee } from "../components/Marquee";
import { VaultCard } from "../components/VaultCard";
import { RoyalListForm } from "../components/RoyalListForm";

const HERO_IMG = "https://images.unsplash.com/photo-1648838449733-28f3348232e3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const PORTRAIT = "https://images.unsplash.com/photo-1764265928226-4ee4e583e9b8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className="block overflow-hidden">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Home() {
  const [data, setData] = useState(null);
  const site = useSite();
  const heroImg = site.hero_image || HERO_IMG;
  const heroTagline = site.hero_tagline || "Soul. Story. Style. No permission required.";
  const meetPortrait = site.meet_portrait || PORTRAIT;
  const { playTrack, track, playing } = usePlayer();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.85], [1, 0.15]);

  useSEO("", "Step inside the world of Queen Denae — Southern soul, storytelling, and The Royal Vault: her private archive of unreleased music and rare recordings.", heroImg);

  useEffect(() => {
    api.get("/home").then((r) => setData(r.data)).catch(() => setData({}));
  }, []);

  const featured = data?.featured;
  const featuredPlaying = track?.slug === featured?.slug && playing;

  return (
    <div data-testid="home-page">
      {/* SECTION 1 — CINEMATIC HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-end overflow-hidden">
        <motion.div style={{ y: heroY, opacity: heroOpacity }} className="absolute inset-0">
          <img src={heroImg} alt="Queen Denae" className="w-full h-full object-cover object-top animate-slow-zoom" />
          <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/40 to-obsidian/30" />
          <div className="absolute inset-0 bg-gradient-to-r from-obsidian/70 via-transparent to-transparent" />
        </motion.div>
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 pb-24 sm:pb-32 w-full">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="font-mono text-[11px] tracking-[0.4em] uppercase text-goldglow mb-6"
          >
            Southern Soul · Storytelling · The Royal Vault
          </motion.p>
          <h1 className="font-serif tracking-tight leading-[0.9] text-cream text-[17vw] sm:text-[13vw] lg:text-[9.5rem]" data-testid="hero-headline">
            <MaskedLine delay={0.5}>QUEEN</MaskedLine>
            <MaskedLine delay={0.68} className="italic text-goldglow">DENAE</MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.8 }}
            className="mt-6 font-serif italic text-xl sm:text-2xl text-cream/85 max-w-xl"
            data-testid="hero-tagline"
          >
            {heroTagline}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.55, duration: 0.8 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link
              to="/vault"
              data-testid="hero-enter-vault-btn"
              className="h-14 px-9 inline-flex items-center bg-gold text-obsidian font-mono text-[11px] tracking-[0.3em] uppercase hover:bg-goldglow transition-colors duration-300"
            >
              Enter the Royal Vault
            </Link>
            <Link
              to="/about"
              data-testid="hero-explore-btn"
              className="h-14 px-9 inline-flex items-center border border-cream/40 text-cream font-mono text-[11px] tracking-[0.3em] uppercase hover:border-gold hover:text-gold transition-colors duration-300"
            >
              Explore Queen Denae
            </Link>
          </motion.div>
        </div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.2 }}
          className="absolute bottom-8 right-8 hidden sm:flex flex-col items-center gap-2 text-sand/60"
        >
          <span className="font-mono text-[9px] tracking-[0.3em] uppercase rotate-90 origin-center translate-y-[-8px]">Scroll</span>
          <ArrowDown size={14} className="mt-6 animate-bounce" />
        </motion.div>
      </section>

      <Marquee items={["The Royal Vault", "Southern Soul", "Queen of Unfiltered Soul", "No Permission Required", "Grown Woman Music", "Straight From The Queen"]} />

      {/* SECTION 2 — CURRENT MOMENT */}
      <section className="py-20 sm:py-32" data-testid="current-moment-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <SectionLabel>Current Moment</SectionLabel>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-16 items-center">
            <Reveal className="md:col-span-6">
              <div className="relative overflow-hidden border border-gold/20">
                {featured?.artwork_url ? (
                  <motion.img
                    initial={{ scale: 1.12 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
                    src={resolveMedia(featured.artwork_url)}
                    alt={featured?.title || "Featured release"}
                    data-testid="campaign-artwork"
                    className="w-full aspect-square object-cover"
                  />
                ) : (
                  <div className="w-full aspect-square bg-velvet" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian/60 to-transparent" />
                <span className="absolute top-5 left-5 font-mono text-[10px] tracking-[0.3em] uppercase text-goldglow bg-obsidian/70 backdrop-blur px-3 py-1.5">
                  A Queen Denae Royal Vault Exclusive
                </span>
              </div>
            </Reveal>
            <div className="md:col-span-6">
              <Reveal>
                <h2 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight text-cream" data-testid="campaign-title">
                  {featured?.title || "BOUNCE BACK"}
                </h2>
                <p className="mt-6 font-serif italic text-2xl sm:text-3xl text-goldglow/90 leading-snug" data-testid="campaign-quote">
                  "They counted me out.<br />I kept counting myself back in."
                </p>
                <p className="mt-6 text-sand leading-relaxed max-w-md">
                  {featured?.description || "A rare recording from the Royal Vault — the Queen's private archive."}
                </p>
                <div className="mt-9 flex flex-wrap items-center gap-4">
                  <button
                    onClick={() => featured && playTrack({ ...featured, tag: "The Royal Vault" })}
                    data-testid="campaign-play-btn"
                    className="h-14 w-14 rounded-full bg-gold text-obsidian flex items-center justify-center hover:bg-goldglow transition-colors duration-300"
                    aria-label="Play featured track"
                  >
                    {featuredPlaying ? <Pause size={20} /> : <Play size={20} className="ml-0.5" />}
                  </button>
                  <Link
                    to={`/vault/${featured?.slug || "bounce-back"}`}
                    data-testid="campaign-listen-btn"
                    className="h-14 px-8 inline-flex items-center border border-gold/50 text-gold font-mono text-[11px] tracking-[0.3em] uppercase hover:bg-gold hover:text-obsidian transition-colors duration-300"
                  >
                    Listen in the Royal Vault
                  </Link>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3 — ROYAL VAULT PREVIEW */}
      <section className="py-20 sm:py-32 bg-gallery" data-testid="vault-preview-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
            <div className="lg:col-span-5">
              <Reveal>
                <SectionLabel>The Archive</SectionLabel>
                <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-cream" data-testid="vault-preview-heading">
                  THE<br />ROYAL<br /><span className="italic text-goldglow">VAULT</span>
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-7 lg:pt-16">
              <Reveal delay={0.15}>
                <p className="text-lg text-sand leading-relaxed max-w-xl">
                  Not everything I create is made for streaming.
                </p>
                <div className="mt-6 grid grid-cols-2 gap-x-8 gap-y-2 max-w-xl">
                  <p className="font-serif italic text-cream/80">Some songs capture a moment.</p>
                  <p className="font-serif italic text-cream/80">Some tell stories I wanted to preserve.</p>
                  <p className="font-serif italic text-cream/80 col-span-2">Some are original recordings, demos, and ideas that belong to a different chapter of my creative journey.</p>
                </div>
                <p className="mt-6 text-lg text-cream leading-relaxed max-w-xl">
                  The Royal Vault is my private archive of unreleased Queen Denae music — songs and recordings that I've chosen to preserve outside of my official release catalog.
                </p>
                <p className="mt-5 font-serif italic text-cream/80 leading-relaxed max-w-xl">
                  They may be revisited. Reimagined. Re-recorded. Or they may remain exactly where they are. Either way, they are part of the story.
                </p>
                <p className="mt-6 font-serif text-xl sm:text-2xl text-goldglow leading-snug">
                  The Royal Vault preserves the work.<br />The official records make the statements.
                </p>
              </Reveal>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {(data?.vault || []).map((entry, i) => (
              <VaultCard key={entry.id} entry={entry} index={i} />
            ))}
          </div>
          <Reveal className="mt-12 text-center">
            <Link
              to="/vault"
              data-testid="vault-preview-cta"
              className="inline-flex h-14 px-10 items-center gap-3 border border-gold/50 text-gold font-mono text-[11px] tracking-[0.3em] uppercase hover:bg-gold hover:text-obsidian transition-colors duration-300"
            >
              Enter the Royal Vault <ArrowRight size={14} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* SECTION 5 — OFFICIAL MUSIC */}
      <section className="py-20 sm:py-32" data-testid="official-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <SectionLabel>The Official Records</SectionLabel>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7">
              <Reveal>
                <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-cream" data-testid="official-heading">
                  THE OFFICIAL<br /><span className="italic text-goldglow">RECORDS</span>
                </h2>
                <p className="mt-6 text-lg text-sand max-w-lg leading-relaxed">
                  The Vault is where I create freely.<br />
                  The official records are where I make my statements.
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-5">
              <Reveal delay={0.15}>
                <div className="border border-gold/20 bg-gallery p-8 sm:p-10" data-testid="official-coming-soon-card">
                  <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-gold/70 mb-4">Upcoming Release</p>
                  <p className="font-serif text-3xl sm:text-4xl text-cream tracking-tight">
                    {data?.official_upcoming?.title || "NEW MUSIC COMING SOON"}
                  </p>
                  <p className="mt-4 text-sm text-sand leading-relaxed">
                    {data?.official_upcoming?.release_date
                      ? `Arriving ${data.official_upcoming.release_date}`
                      : "The next official record is on the way. Join the Royal List to hear it first."}
                  </p>
                  <Link
                    to="/official-music"
                    data-testid="official-explore-btn"
                    className="mt-7 inline-flex h-12 px-7 items-center gap-2 border border-cream/30 text-cream font-mono text-[10px] tracking-[0.3em] uppercase hover:border-gold hover:text-gold transition-colors duration-300"
                  >
                    Official Music <ArrowRight size={13} />
                  </Link>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 6 — LATEST FROM QUEEN DENAE */}
      <section className="py-20 sm:py-32 bg-gallery" data-testid="latest-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <SectionLabel>Latest From Queen Denae</SectionLabel>
          <Reveal>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-cream mb-14" data-testid="latest-heading">
              FROM THE WORLD OF<br /><span className="italic text-goldglow">QUEEN DENAE</span>
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {(data?.content || []).map((item, i) => (
              <Reveal key={item.id} delay={i * 0.1}>
                <div data-testid={`content-card-${i}`} className="group border border-gold/15 hover:border-gold/40 bg-obsidian transition-colors duration-500">
                  <div className="relative overflow-hidden aspect-[16/10]">
                    {item.image_url && (
                      <img src={resolveMedia(item.image_url)} alt={item.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    )}
                    <span className="absolute top-4 left-4 font-mono text-[9px] tracking-[0.3em] uppercase text-goldglow bg-obsidian/70 backdrop-blur px-3 py-1.5">
                      {item.type}
                    </span>
                  </div>
                  <div className="p-6">
                    <h3 className="font-serif text-xl text-cream group-hover:text-goldglow transition-colors duration-300">{item.title}</h3>
                    <p className="mt-2 text-sm text-sand leading-relaxed line-clamp-2">{item.description}</p>
                    {item.link_url && (
                      <Link to={item.link_url} className="mt-4 inline-block font-mono text-[10px] tracking-[0.25em] uppercase text-gold/80 hover:text-gold">
                        View →
                      </Link>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 7 — JOIN THE ROYAL LIST */}
      <section className="py-20 sm:py-32 relative overflow-hidden" data-testid="royal-list-section">
        <div className="absolute inset-0 bg-wine/20" />
        <div className="absolute inset-0 bg-gradient-to-b from-obsidian via-transparent to-obsidian" />
        <div className="relative max-w-3xl mx-auto px-5 sm:px-8 text-center">
          <SectionLabel center>The Inner Circle</SectionLabel>
          <Reveal>
            <h2 className="font-serif text-4xl sm:text-6xl tracking-tight text-cream" data-testid="royal-list-heading">
              JOIN THE<br /><span className="italic text-goldglow">ROYAL LIST</span>
            </h2>
            <div className="mt-8 space-y-1.5 text-sand">
              {["Unreleased music.", "New releases. Royal Vault drops.", "Shows. Stories.", "And whatever else I feel like creating."].map((l) => (
                <p key={l} className="font-serif italic text-lg sm:text-xl text-cream/80">{l}</p>
              ))}
              <p className="pt-3 font-mono text-[11px] tracking-[0.35em] uppercase text-gold">Straight from the Queen.</p>
            </div>
            <div className="mt-10">
              <RoyalListForm source="homepage" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* SECTION 8 — MEET QUEEN DENAE */}
      <section className="py-20 sm:py-32 bg-gallery" data-testid="meet-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-16 items-center">
            <Reveal className="md:col-span-5">
              <div className="relative overflow-hidden border border-gold/20">
                <motion.img
                  initial={{ scale: 1.1 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
                  src={meetPortrait}
                  alt="Queen Denae portrait"
                  data-testid="meet-portrait"
                  className="w-full aspect-[4/5] object-cover"
                />
              </div>
            </Reveal>
            <div className="md:col-span-7">
              <Reveal delay={0.1}>
                <SectionLabel>The Woman, The World</SectionLabel>
                <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-cream" data-testid="meet-heading">
                  MEET<br /><span className="italic text-goldglow">QUEEN DENAE</span>
                </h2>
                <p className="mt-8 text-lg text-cream/85 leading-relaxed max-w-xl">
                  Queen Denae makes music for grown women who have lived enough life to know exactly who they are — or are finally giving themselves permission to find out.
                </p>
                <p className="mt-5 font-mono text-[11px] tracking-[0.3em] uppercase text-gold/80">
                  Southern soul · Storytelling · Humor · Truth · Reinvention
                </p>
                <p className="mt-5 text-lg text-sand">The music is only one part of the world.</p>
                <Link
                  to="/about"
                  data-testid="meet-story-btn"
                  className="mt-9 inline-flex h-14 px-9 items-center gap-3 bg-gold text-obsidian font-mono text-[11px] tracking-[0.3em] uppercase hover:bg-goldglow transition-colors duration-300"
                >
                  My Story <ArrowRight size={14} />
                </Link>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
